import { Fragment, useState } from "react";
import { useSearchParams } from "react-router";
import {
  useExportRecords,
  useExportStats,
  useRetryExport,
  useBulkRetryExports,
  useDismissExport,
} from "../hooks/useExportRecords";
import { useExportPayload } from "../hooks/useExportPayload";
import { useJobTypes } from "../hooks/useJobTypes";
import { useDestinationHealth } from "../hooks/useDestinationHealth";
import { DestinationHealthBanner } from "../components/DestinationHealthBanner";
import { toast } from "@/components/Toaster";
import "../admin.css";
import { humanizeExportStatus } from "./export-history-labels";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// E-056 — cluster contiguous records of one export run: same batch, adjacent
// in the (createdAt-desc) list, and created within this window of each other.
// One release fans out N destinations × M docs within seconds; a gap-based
// cluster avoids splitting a run across a fixed time-bucket boundary.
const RUN_GAP_MS = 60_000;

interface ExportRun<R> {
  key: string;
  batchLabel: string;
  records: R[];
}

/** Group list-ordered records into runs; null when batch identity is absent
 *  (stale cache / old server) so the flat table renders unchanged. */
function groupIntoRuns<R extends { id: string; batchId?: string; batchLabel?: string; createdAt: string }>(
  records: R[],
): ExportRun<R>[] | null {
  if (records.length === 0 || records.some((r) => !r.batchId)) return null;
  const runs: ExportRun<R>[] = [];
  for (const rec of records) {
    const prev = runs[runs.length - 1];
    const prevRec = prev?.records[prev.records.length - 1];
    const sameRun =
      prevRec != null &&
      prevRec.batchId === rec.batchId &&
      Math.abs(new Date(prevRec.createdAt).getTime() - new Date(rec.createdAt).getTime()) <= RUN_GAP_MS;
    if (sameRun) {
      prev!.records.push(rec);
    } else {
      runs.push({ key: rec.id, batchLabel: rec.batchLabel ?? "", records: [rec] });
    }
  }
  return runs;
}

/** "2 delivered · 1 dead letter" — first thing read on a run header. */
function summarizeRun(records: Array<{ status: string }>): string {
  const counts = new Map<string, number>();
  for (const r of records) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
  const parts: string[] = [];
  const delivered = counts.get("success") ?? 0;
  if (delivered > 0) parts.push(`${delivered} delivered`);
  for (const [status, n] of counts) {
    if (status === "success") continue;
    parts.push(`${n} ${humanizeExportStatus(status).toLowerCase()}`);
  }
  return parts.join(" · ");
}

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "retrying", label: "Retrying" },
  { value: "dead_letter", label: "Dead Letter" },
  { value: "dismissed", label: "Dismissed" },
];

/** E-057 — one chip per active scope filter (destination / batch / job type). */
function FilterChip({
  label,
  value,
  testId,
  clearLabel,
  onClear,
}: {
  label: string;
  value: string;
  testId: string;
  clearLabel: string;
  onClear: () => void;
}) {
  return (
    <span className="export-filter-chip" data-testid={testId}>
      {label}: {value}
      <button type="button" className="rb-btn-link" aria-label={clearLabel} onClick={onClear}>
        ✕
      </button>
    </span>
  );
}

/** E-057 — mounted only when ?jobTypeId= is set, so the job-types fetch
 *  never runs on the (common) unfiltered path. */
function JobTypeFilterChip({ id, onClear }: { id: string; onClear: () => void }) {
  const { items } = useJobTypes();
  return (
    <FilterChip
      label="Job type"
      value={items.find((jt) => jt.id === id)?.name ?? id}
      testId="job-type-filter-chip"
      clearLabel="Clear job type filter"
      onClear={onClear}
    />
  );
}

export function ExportHistoryPage() {
  // F-036 — the destination setup surface deep-links here with
  // ?destinationId= instead of embedding its own runtime-errors tab.
  // E-057 — the queue lane badge (?jobTypeId=) and the batch-detail rollup
  // (?batchId=) deep-link here too, optionally pre-filtered via ?status=.
  // ALL four filters live in the URL (one source of truth — shareable,
  // back/forward-safe); the status select writes the URL too.
  const [searchParams, setSearchParams] = useSearchParams();
  const destinationFilter = searchParams.get("destinationId") ?? undefined;
  const batchFilter = searchParams.get("batchId") ?? undefined;
  const jobTypeFilter = searchParams.get("jobTypeId") ?? undefined;
  const statusFilter = searchParams.get("status") ?? "";
  const [page, setPage] = useState(0);
  const limit = 50;

  const setParam = (name: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(name, value);
      else next.delete(name);
      return next;
    });
    setPage(0);
  };

  const { records, total, isLoading, refresh } = useExportRecords({
    status: statusFilter || undefined,
    destinationId: destinationFilter,
    batchId: batchFilter,
    jobTypeId: jobTypeFilter,
    limit,
    offset: page * limit,
  });
  const { stats } = useExportStats();
  // E-056 follow-on — per-destination failing streaks (dead-letter age/repeat).
  const { destinations: failingDestinations } = useDestinationHealth();
  const { retry } = useRetryExport();
  const { bulkRetry } = useBulkRetryExports();
  const { dismiss } = useDismissExport();

  // E-027 — payload preview/download. `openPayloadId` tracks which record's
  // panel is expanded; the payload itself is fetched lazily (and cached) by
  // useExportPayload once the operator opens it.
  const [openPayloadId, setOpenPayloadId] = useState<string | null>(null);
  const { payloads, load: loadPayload } = useExportPayload();

  const handleTogglePayload = (id: string) => {
    if (openPayloadId === id) {
      setOpenPayloadId(null);
      return;
    }
    setOpenPayloadId(id);
    loadPayload(id);
  };

  const handleRetry = async (id: string) => {
    await retry(id);
    refresh();
  };

  const handleDismiss = async (id: string) => {
    await dismiss(id);
    refresh();
  };

  const deadLetterCount = stats.dead_letter ?? 0;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Export History</h1>
      </div>

      {/* E-056 follow-on — a destination failing identically for weeks is
          announced up here, not buried in scrollable history rows. "View
          history" applies the E-057 URL-driven destination filter (chip
          appears, shareable, back/forward-safe) via the shared setParam. */}
      <DestinationHealthBanner
        destinations={failingDestinations}
        onSelectDestination={(id) => setParam("destinationId", id)}
      />

      {/* Stats bar */}
      <div className="export-stats-bar">
        {Object.entries(stats).map(([status, count]) => (
          <div key={status} className={`export-stat export-stat--${status}`}>
            <span className="export-stat-count">{count}</span>
            <span className="export-stat-label">{humanizeExportStatus(status)}</span>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="export-filter-bar">
        {destinationFilter && (
          <FilterChip
            label="Destination"
            value={records[0]?.destinationName ?? destinationFilter}
            testId="destination-filter-chip"
            clearLabel="Clear destination filter"
            onClear={() => setParam("destinationId", null)}
          />
        )}
        {/* E-057 — batch / job-type scope chips (deep-linked from the batch
            detail rollup and the queue lane's failed badge). */}
        {batchFilter && (
          <FilterChip
            label="Batch"
            value={records[0]?.batchLabel ?? batchFilter}
            testId="batch-filter-chip"
            clearLabel="Clear batch filter"
            onClear={() => setParam("batchId", null)}
          />
        )}
        {jobTypeFilter && (
          <JobTypeFilterChip id={jobTypeFilter} onClear={() => setParam("jobTypeId", null)} />
        )}
        <select
          className="rb-select"
          value={statusFilter}
          onChange={(e) => setParam("status", e.target.value || null)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {deadLetterCount > 0 && (
          <>
            <button
              className="rb-btn rb-btn-danger"
              onClick={() => setParam("status", "dead_letter")}
            >
              Dead Letters ({deadLetterCount})
            </button>
            {statusFilter === "dead_letter" && records.length > 0 && (
              <button
                className="rb-btn"
                onClick={async () => {
                  try {
                    const { count } = await bulkRetry();
                    refresh();
                    toast.success(
                      count > 0
                        ? `Re-enqueued ${count} dead letter export${count === 1 ? "" : "s"}`
                        : "No dead letters to retry",
                    );
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : "Bulk retry failed");
                  }
                }}
              >
                Bulk Retry All ({deadLetterCount})
              </button>
            )}
          </>
        )}
      </div>

      {isLoading && <div className="admin-loading"><div className="loading-spinner" /></div>}

      {records.length > 0 && (() => {
        // E-056 — group into export runs when batch identity is present.
        const runs = groupIntoRuns(records);
        const renderRecordRow = (rec: (typeof records)[number]) => {
          const payloadOpen = openPayloadId === rec.id;
          const hasDetail =
            Boolean(rec.lastError) ||
            Boolean(rec.transformErrors && rec.transformErrors.length > 0) ||
            payloadOpen;
          const payload = payloads[rec.id];
          return (
            <Fragment key={rec.id}>
              <tr
                className={`export-record export-record--${rec.status}${hasDetail ? " export-record--has-detail" : ""}`}
              >
                <td className="export-record-status">
                  <span className={`export-status-badge export-status-badge--${rec.status}`}>
                    {humanizeExportStatus(rec.status)}
                  </span>
                </td>
                {/* E-056 — displayId-led doc label; raw filename in the title. */}
                <td className="export-record-doc" title={rec.documentFilename}>
                  {rec.documentLabel ?? rec.documentFilename}
                </td>
                <td className="export-record-batch">{rec.batchLabel ?? "—"}</td>
                <td className="export-record-dest">{rec.destinationName}</td>
                <td className="export-record-meta">
                  <span className="export-record-time">
                    {rec.exportedAt ? new Date(rec.exportedAt).toLocaleString() : new Date(rec.createdAt).toLocaleString()}
                  </span>
                  {rec.retryCount > 0 && (
                    <span className="export-record-retries">{rec.retryCount} retries</span>
                  )}
                </td>
                <td>
                  <div className="export-record-actions">
                    {(rec.status === "failed" || rec.status === "dead_letter") && (
                      <button className="rb-btn" onClick={() => handleRetry(rec.id)}>Retry</button>
                    )}
                    {rec.status === "dead_letter" && (
                      <button className="rb-btn" onClick={() => handleDismiss(rec.id)}>Dismiss</button>
                    )}
                    {rec.hasPayload && (
                      <button className="rb-btn" onClick={() => handleTogglePayload(rec.id)}>
                        {payloadOpen ? "Hide payload" : "View payload"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {hasDetail && (
                <tr className="export-record-detail-row">
                  <td colSpan={6}>
                    <div className="export-record-detail">
                      {rec.lastError && (
                        <div className="export-error-block" title={rec.lastError}>
                          {rec.lastError}
                        </div>
                      )}
                      {rec.transformErrors && rec.transformErrors.length > 0 && (
                        <div className="export-error-block export-error-block--transform">
                          {rec.transformErrors.map((te, i) => (
                            <div key={i} className="export-error-line" title={te.message}>
                              Transform · <code>{te.targetColumn}</code>: {te.message}
                            </div>
                          ))}
                        </div>
                      )}
                      {payloadOpen && (
                        <div className="export-payload-panel">
                          {!payload ? (
                            <span className="export-payload-loading">Loading payload…</span>
                          ) : (
                            <>
                              <div className="export-payload-meta">
                                <span>{payload.contentType}</span>
                                {payload.sizeBytes != null && <span>{formatBytes(payload.sizeBytes)}</span>}
                                {payload.truncated && (
                                  <span className="export-payload-truncated">
                                    Truncated — payload exceeded the persisted-payload size cap
                                  </span>
                                )}
                                <a
                                  className="rb-btn-link"
                                  href={payload.objectUrl}
                                  download={`export-${rec.id}`}
                                >
                                  Download
                                </a>
                              </div>
                              <pre className="export-payload-content">{payload.content}</pre>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        };
        return (
          <div className="export-table-wrap">
            <table className="rb-table export-record-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Document</th>
                  <th>Batch</th>
                  <th>Destination</th>
                  <th>Exported</th>
                  <th className="export-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs
                  ? runs.map((run) => (
                      <Fragment key={run.key}>
                        {/* E-056 — one export run reads as one thing: batch
                            identity + when + "N delivered · M dead letter". */}
                        <tr className="export-run-header" data-testid="export-run-header">
                          <td colSpan={6}>
                            <span className="export-run-batch">{run.batchLabel}</span>
                            <span className="export-run-time">
                              {new Date(run.records[0]!.createdAt).toLocaleString()}
                            </span>
                            <span className="export-run-summary">{summarizeRun(run.records)}</span>
                          </td>
                        </tr>
                        {run.records.map(renderRecordRow)}
                      </Fragment>
                    ))
                  : records.map(renderRecordRow)}
              </tbody>
            </table>
          </div>
        );
      })()}

      {!isLoading && records.length === 0 && (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">No export records found</span>
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="export-pagination">
          <button className="rb-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span className="export-page-info">
            {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
          </span>
          <button className="rb-btn" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
